import { useState, useEffect, useCallback } from 'react';

export interface DateRangeFilterValue {
  from: string;
  to: string;
}

export interface UseDateRangeFilterOptions {
  /** Unique key for session storage persistence */
  storageKey: string;
}

export interface UseDateRangeFilterReturn {
  /** Current filter values */
  value: DateRangeFilterValue;
  /** Whether the date range is valid (from <= to) */
  isValid: boolean;
  /** Validation error message if invalid */
  validationError: string | null;
  /** Whether any filter is active */
  hasFilter: boolean;
  /** Set the from date */
  setFrom: (date: string) => void;
  /** Set the to date */
  setTo: (date: string) => void;
  /** Clear both date filters */
  clear: () => void;
}

// Helper to get session storage key for date filters
const getDateFilterStorageKey = (key: string) => `date-filter-${key}`;

// Helper to parse stored date filters from session storage
const getStoredDateFilters = (key: string): DateRangeFilterValue => {
  try {
    const stored = sessionStorage.getItem(getDateFilterStorageKey(key));
    if (stored) {
      const parsed = JSON.parse(stored);
      return { from: parsed.from || '', to: parsed.to || '' };
    }
  } catch {
    // Ignore parse errors
  }
  return { from: '', to: '' };
};

// Helper to save date filters to session storage
const saveDateFilters = (key: string, from: string, to: string) => {
  sessionStorage.setItem(
    getDateFilterStorageKey(key),
    JSON.stringify({ from, to })
  );
};

/**
 * Hook for managing date range filter state with session persistence and validation.
 * 
 * @example
 * ```tsx
 * const dateFilter = useDateRangeFilter({ storageKey: `writeoff-${centerId}` });
 * 
 * // Use in query params
 * const queryParams = {
 *   centerId,
 *   ...(dateFilter.isValid && dateFilter.value.from && { serviceDateFrom: dateFilter.value.from }),
 *   ...(dateFilter.isValid && dateFilter.value.to && { serviceDateTo: dateFilter.value.to }),
 * };
 * 
 * // Render the filter component
 * <DateRangeFilter filter={dateFilter} label="Service Date Range" />
 * ```
 */
export function useDateRangeFilter({ storageKey }: UseDateRangeFilterOptions): UseDateRangeFilterReturn {
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [validationError, setValidationError] = useState<string | null>(null);

  // Load stored date filters when storage key changes
  useEffect(() => {
    if (storageKey) {
      const stored = getStoredDateFilters(storageKey);
      setDateFrom(stored.from);
      setDateTo(stored.to);
    }
  }, [storageKey]);

  // Validate date range
  useEffect(() => {
    if (dateFrom && dateTo) {
      const fromDate = new Date(dateFrom);
      const toDate = new Date(dateTo);
      if (fromDate > toDate) {
        setValidationError('From date cannot be after To date');
        return;
      }
    }
    setValidationError(null);
  }, [dateFrom, dateTo]);

  const setFrom = useCallback((date: string) => {
    setDateFrom(date);
    saveDateFilters(storageKey, date, dateTo);
  }, [storageKey, dateTo]);

  const setTo = useCallback((date: string) => {
    setDateTo(date);
    saveDateFilters(storageKey, dateFrom, date);
  }, [storageKey, dateFrom]);

  const clear = useCallback(() => {
    setDateFrom('');
    setDateTo('');
    setValidationError(null);
    saveDateFilters(storageKey, '', '');
  }, [storageKey]);

  return {
    value: { from: dateFrom, to: dateTo },
    isValid: !validationError,
    validationError,
    hasFilter: !!(dateFrom || dateTo),
    setFrom,
    setTo,
    clear,
  };
}
